import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {SURVEY_REVIEW_STATE_LABEL_KEYS} from "../../../common/uiElements/SurveyReviewStateElement";
import {surveyNpsClassLabelKey} from "../../../common/uiElements/SurveyNpsClassElement";
import {roundSurveyScore} from "../../../common/uiElements/SurveyScoreElement";
import {
    isAnswerableQuestion,
    submissionQuestions,
    SurveyAnswerContext,
    surveyAnswerToText
} from "../../shared/surveyAnswer.util";
import {
    exportDaySlug,
    slugifyForFileName,
    type SurveyExportBranding
} from "../../shared/surveyExport.util";
import {surveyMarkdownToPlainText} from "../../shared/surveyMarkdownText.util";
import {
    surveyQuestionMetaText,
    surveyQuestionTitleWithMarkers,
    surveySensitiveLabel
} from "../../shared/surveyQuestionMarkers.util";
import {getSurveyQuestionTypeLabelKey} from "../../shared/surveyQuestionTypes.util";

export type SurveySubmissionsDatasetFormat = 'xlsx' | 'csv';

/** Guards against pulling an unbounded history into the browser. */
export const SUBMISSIONS_MAX_EXPORT_ROWS = 5000;

export type SurveySubmissionsDatasetColumnKind = 'text' | 'date' | 'number';

export type SurveySubmissionsDatasetColumn = {
    /** Stable across exports: a fixed name, or `question:<questionId>` for an answer column. */
    id: string
    label: string
    kind: SurveySubmissionsDatasetColumnKind
    width: number
};

export type SurveySubmissionsDatasetCell = string | number | Date | undefined;

export type SurveySubmissionsDataset = {
    sheetName: string
    infoSheetName: string
    title: string
    infoLines: {label: string, value: string}[]
    columns: SurveySubmissionsDatasetColumn[]
    rows: SurveySubmissionsDatasetCell[][]
    branding?: SurveyExportBranding
    truncatedLabel?: string
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export type SurveySubmissionsDatasetInput = {
    survey?: Survey
    /*
        Collected by the table controller, so an export always carries the list's own search,
        filters, and sorting rather than a second request shape that can drift from it.
     */
    submissions: AggregationSurveySubmissionListItem[]
    /** Server total before the row cap, so a shortened export can say so. */
    total: number
    ctx: SurveyAnswerContext
    t: Translate
    organizationName?: string
    branding?: SurveyExportBranding
    /** Off by default, matching the evaluation export's gate on flagged questions. */
    includeSensitive: boolean
    now?: Date
    /**
     * The columns to write, in this order (ids from `surveySubmissionsDatasetColumnOptions`); every column when
     * missing. An id the dataset has no column for - a switched-off question none of these rows answered - is skipped.
     */
    columnIds?: string[]
};

type ColumnOptionsInput = Pick<SurveySubmissionsDatasetInput, 'survey' | 'ctx' | 't'>;

const fixedColumns = (t: Translate): SurveySubmissionsDatasetColumn[] => [
    {id: 'submitted', label: t('Submitted'), kind: 'date', width: 20},
    {id: 'profile', label: t('Profile'), kind: 'text', width: 24},
    {id: 'score', label: t('Score'), kind: 'number', width: 10},
    {id: 'nps', label: t('NPS'), kind: 'text', width: 14},
    {id: 'sector', label: t('Sector'), kind: 'text', width: 20},
    {id: 'reservation', label: t('Reservation code'), kind: 'text', width: 18},
    {id: 'language', label: t('Language'), kind: 'text', width: 14},
    {id: 'version', label: t('Version'), kind: 'number', width: 10},
    {id: 'reviewState', label: t('Review state'), kind: 'text', width: 16},
    {id: 'resolvedAt', label: t('Resolved at'), kind: 'date', width: 20},
    {id: 'resolvedBy', label: t('Resolved by'), kind: 'text', width: 20},
    {id: 'notes', label: t('Notes'), kind: 'text', width: 40},
    {id: 'id', label: t('ID'), kind: 'text', width: 26},
];

const questionColumnId = (question: SurveyQuestion) => `question:${question.id}`;

/*
    One row per submission and one column per question, so the question type cannot be its own
    column - it is annotated onto the header instead, behind the title's own Required marker:
    "How was your stay? * (Score 1-10 · Sensitive)".

    Sensitive annotates a flagged question ALWAYS, never only when its answers came out masked.
    Marking the question and masking the value are two different facts, and tying them together
    left the one artefact that actually carries the unmasked answers as the only one that never
    said they were sensitive. Nothing is needed for the masked case: those cells each spell out
    `hiddenLabel` in full, so a second header token would only repeat the sentence below it.
*/
const answerColumn = (question: SurveyQuestion, {ctx, t}: ColumnOptionsInput): SurveySubmissionsDatasetColumn => {
    const annotations = surveyQuestionMetaText(
        t(getSurveyQuestionTypeLabelKey(question.type)),
        surveySensitiveLabel(question, ctx),
    );

    return {
        id: questionColumnId(question),
        label: `${surveyQuestionTitleWithMarkers(question, ctx)} (${annotations})`,
        kind: 'text',
        width: 30,
    };
};

/**
 * Every column an export of `survey` can carry, for the export dialog's picker: the fixed ones, then one per
 * answerable question - switched-off ones too, since the rows decide only later whether one was answered.
 */
export const surveySubmissionsDatasetColumnOptions = (input: ColumnOptionsInput): {id: string, label: string}[] => [
    ...fixedColumns(input.t),
    ...(input.survey?.questions ?? []).filter(isAnswerableQuestion).map(question => answerColumn(question, input)),
].map(({id, label}) => ({id, label}));

/** Keeps the picked columns in the picked order, cells included. */
const pickColumns = (
    columns: SurveySubmissionsDatasetColumn[],
    rows: SurveySubmissionsDatasetCell[][],
    columnIds: string[]
) => {
    const indexes = columnIds
        .map(id => columns.findIndex(column => column.id === id))
        .filter(index => index >= 0);

    return {
        columns: indexes.map(index => columns[index]),
        rows: rows.map(row => indexes.map(index => row[index])),
    };
};

export const buildSurveySubmissionsDataset = (
    input: SurveySubmissionsDatasetInput
): SurveySubmissionsDataset => {
    const {survey, submissions, total, ctx, t} = input;
    const generatedAt = input.now ?? new Date();

    // A switched-off question keeps its column only while one of these rows answered it.
    const questions = submissionQuestions(survey?.questions, submissions.flatMap(submission => submission.answers ?? []));
    const hiddenLabel = t('Sensitive answers are hidden by default.');

    const staticColumns = fixedColumns(t);
    const answerColumns = questions.map(question => answerColumn(question, input));

    const allRows = submissions.map(submission => {
        const npsLabelKey = surveyNpsClassLabelKey(submission.npsClass);

        const staticCells: SurveySubmissionsDatasetCell[] = [
            submission.createdAt,
            submission.profileName,
            submission.score == null ? undefined : roundSurveyScore(submission.score),
            npsLabelKey ? t(npsLabelKey) : undefined,
            submission.sectorName,
            submission.reservationLabel,
            submission.language
                ? (getNameForLanguage(submission.language, true) || submission.language)
                : undefined,
            submission.surveyVersion,
            t(SURVEY_REVIEW_STATE_LABEL_KEYS[submission.reviewState ?? SurveyReviewState.OPEN]),
            submission.reviewedAt,
            submission.reviewedByName,
            surveyMarkdownToPlainText(submission.reviewNotes)?.trim() || undefined,
            submission.id,
        ];

        const answerCells: SurveySubmissionsDatasetCell[] = questions.map(question => {
            if (question.isSensitive && !input.includeSensitive) return hiddenLabel;

            return surveyAnswerToText(
                submission.answers?.find(answer => answer.questionId === question.id),
                question,
                ctx
            ) || undefined;
        });

        return [...staticCells, ...answerCells];
    });

    const truncated = total > submissions.length;
    const allColumns = [...staticColumns, ...answerColumns];
    const {columns, rows} = input.columnIds
        ? pickColumns(allColumns, allRows, input.columnIds)
        : {columns: allColumns, rows: allRows};

    return {
        sheetName: t('Submissions'),
        infoSheetName: t('Info'),
        title: survey?.name || t('Survey'),
        infoLines: [
            ...(input.organizationName
                ? [{label: t('Organization'), value: input.organizationName}]
                : []),
            {label: t('Submissions'), value: `${submissions.length}`},
            {label: t('Generated'), value: formatDateDMYHM(generatedAt)},
        ],
        columns,
        rows,
        branding: input.branding,
        truncatedLabel: truncated
            ? t('Only the first {{n}} of {{total}} submissions are included.', {
                n: submissions.length,
                total,
            })
            : undefined,
    };
};

export const surveySubmissionsDatasetFileName = (
    survey: Survey | undefined,
    extension: SurveySubmissionsDatasetFormat,
    now: Date
) => `survey-submissions_${slugifyForFileName(survey?.name, 'survey')}_${exportDaySlug(now)}.${extension}`;
